import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istNetzFehler } from '../netzFehler';
import { kopienBegrenzen, plotPfad, statusEmpfangenMerken } from './plotStatus';
import {
    PLOT_AUFTRAG_SPALTEN,
    PLOT_BUCKET,
    PLOT_LINK_GUELTIG_SEK,
    PLOT_MAX_KOPIEN,
    PLOT_MAX_OFFEN_JE_NUTZER,
    PLOT_STATUS_WERTE,
    PlotFehler,
} from './plotTypen';
import type {
    PlotAgent,
    PlotAgentNeu,
    PlotAnlageStatus,
    PlotAuftrag,
    PlotFehlerCode,
    PlotSendenEingabe,
    PlotStatus,
} from './plotTypen';

/**
 * Datenbankzugriff des Plotter-Drucks (SQL Nr. 157/158) — die einzige Stelle,
 * die mit `plot_auftraege`, dem Bucket `plot-auftraege` und den `plot_*`-RPCs
 * redet. Die Regeln ohne Datenbank stehen in plotStatus.ts.
 *
 * Jeder Fehler kommt als `PlotFehler` (plotTypen.ts) mit deutschem Satz:
 *  - die RPCs melden `PLOT_<CODE>: <Klartext>` → Code + Klartext;
 *  - kein Netz (fetch gescheitert, HTTP-Status 0) → `netz`;
 *  - SQL fehlt (PGRST202/42883 Funktion, 42P01/PGRST205 Tabelle, HTTP 404)
 *    → `nicht_eingerichtet`; `plotAnlageStatusLaden` gibt dann `null`,
 *    `plotAuftraegeLaden` eine leere Liste;
 *  - alles andere → `unbekannt` mit der Rohmeldung in Klammern (Diagnose).
 *
 * `supabase` ist im Bestand untypisiert; jede Antwort wird deshalb als
 * `unknown` gelesen und von Hand geprüft.
 */

const TABELLE = 'plot_auftraege';

// ─── Fehler ─────────────────────────────────────────────────────────────────

const feld = (o: unknown, schluessel: string): unknown =>
    o !== null && typeof o === 'object' ? (o as Record<string, unknown>)[schluessel] : undefined;

const fehlerCode = (e: unknown): string => {
    const c = feld(e, 'code');
    return typeof c === 'string' ? c : typeof c === 'number' ? String(c) : '';
};

const fehlerText = (e: unknown): string => {
    const m = feld(e, 'message');
    return typeof m === 'string' ? m : '';
};

const ERSATZ_TEXT: Record<PlotFehlerCode, string> = {
    nicht_eingerichtet: 'Plotter-Druck ist in dieser Umgebung noch nicht eingerichtet.',
    netz: 'Keine Verbindung zum Server — bitte später erneut senden.',
    nicht_angemeldet: 'Nicht angemeldet — bitte neu anmelden und erneut senden.',
    keine_firma: 'Diesem Konto ist keine Firma zugeordnet — Plotter-Druck ist nicht möglich.',
    kein_zugriff: 'Kein Zugriff auf dieses Projekt.',
    kopien: `Die Anzahl der Kopien muss zwischen 1 und ${PLOT_MAX_KOPIEN} liegen.`,
    zu_viele_offen: `Schon ${PLOT_MAX_OFFEN_JE_NUTZER} eigene Aufträge warten auf den Druck — neue erst, wenn welche gedruckt sind.`,
    nicht_mehr_wartend: 'Der Auftrag wartet nicht mehr — er wird schon gedruckt oder ist abgeschlossen.',
    nicht_unklar: 'Der Auftrag ist nicht mehr zu klären — bitte die Liste neu laden.',
    nur_admin: 'Das dürfen nur Admins.',
    // „Datei“ und „Druck“ statt „Plan“: geplottet wird auch die Vorankündigung.
    hochladen: 'Die Datei ließ sich nicht hochladen.',
    rolle_reicht_nicht: 'Die Papierrolle reicht für diesen Druck nicht mehr — bitte an einen Admin wenden.',
    angabe: 'Die Angaben sind unvollständig oder ungültig — bitte prüfen.',
    unbekannt: 'Der Plotter-Auftrag ist fehlgeschlagen.',
};

/**
 * Die Codes aus `plot_auftraege_rpc.sql` (Nr. 158) und `plot_rollen_rpc.sql`
 * (Nr. 170). PLOT_PFAD, PLOT_MASS, PLOT_ID_BELEGT und Neues → `unbekannt`.
 */
const SERVER_CODES: Record<string, PlotFehlerCode> = {
    PLOT_NICHT_ANGEMELDET: 'nicht_angemeldet',
    PLOT_KEINE_FIRMA: 'keine_firma',
    PLOT_KEIN_ZUGRIFF: 'kein_zugriff',
    PLOT_KOPIEN: 'kopien',
    PLOT_ZU_VIELE_OFFEN: 'zu_viele_offen',
    PLOT_NICHT_MEHR_WARTEND: 'nicht_mehr_wartend',
    PLOT_NICHT_UNKLAR: 'nicht_unklar',
    PLOT_NUR_ADMIN: 'nur_admin',
    PLOT_ROLLE_REICHT_NICHT: 'rolle_reicht_nicht',
    PLOT_ROLLE_ANGABE: 'angabe',
    PLOT_NACHTRAG_ANGABE: 'angabe',
};

const SERVER_MELDUNG = /^\s*(PLOT_[A-Z0-9_]+)\s*:\s*([\s\S]*)$/;

/** PlotFehler mit Klartext; ohne Text der deutsche Ersatzsatz des Codes. */
export const fehler = (code: PlotFehlerCode, text?: string): PlotFehler =>
    new PlotFehler(code, text && text.trim() ? text.trim() : ERSATZ_TEXT[code]);

/** SQL Nr. 157/158 fehlt in dieser Umgebung. */
const fehltSql = (e: unknown, httpStatus?: number): boolean => {
    const code = fehlerCode(e);
    if (code === 'PGRST202' || code === '42883' || code === '42P01' || code === 'PGRST205') return true;
    if (httpStatus === 404) return true;
    return /could not find the (function|table)/i.test(fehlerText(e));
};

/**
 * Irgendein Fehler → PlotFehler. Reihenfolge: ein Code vom Server ist
 * eindeutig und geht vor; dann Netz (Status 0 / fetch-Wortlaut); dann
 * fehlendes SQL; sonst `unbekannt`. `unbekanntText` ersetzt den Satz vor der
 * Rohmeldung (Rolle und Nachtrag sind kein „Plotter-Auftrag“).
 */
export const alsPlotFehler = (e: unknown, httpStatus?: number, unbekanntText: string = ERSATZ_TEXT.unbekannt): PlotFehler => {
    if (e instanceof PlotFehler) return e;
    const m = SERVER_MELDUNG.exec(fehlerText(e));
    if (m) return fehler(SERVER_CODES[m[1]] ?? 'unbekannt', m[2]);
    if (httpStatus === 0 || istNetzFehler(e)) return fehler('netz');
    if (fehltSql(e, httpStatus)) return fehler('nicht_eingerichtet');
    const roh = describeError(e);
    return fehler('unbekannt', roh ? `${unbekanntText} (${roh})` : unbekanntText);
};

// ─── Antworten lesen ───────────────────────────────────────────────────────

export interface Antwort {
    data: unknown;
    error: unknown;
    status: number | undefined;
}

export const alsAntwort = (roh: unknown): Antwort => {
    const status = feld(roh, 'status');
    return {
        data: feld(roh, 'data') ?? null,
        error: feld(roh, 'error') ?? null,
        status: typeof status === 'number' ? status : undefined,
    };
};

/** Ruft eine RPC und gibt `data` zurück; jeder Fehler als PlotFehler. */
export const rufe = async (name: string, params?: Record<string, unknown>, unbekanntText?: string): Promise<unknown> => {
    let antwort: Antwort;
    try {
        antwort = alsAntwort(await supabase.rpc(name, params));
    } catch (e) {
        throw alsPlotFehler(e, undefined, unbekanntText);
    }
    if (antwort.error) throw alsPlotFehler(antwort.error, antwort.status, unbekanntText);
    return antwort.data;
};

// ─── Normalisieren ─────────────────────────────────────────────────────────

export type Zeile = Record<string, unknown>;

/** jsonb kommt als Objekt; ein JSON-Text (älterer Treiber, Proxy) wird gelesen. */
export const alsObjekt = (v: unknown): Zeile | null => {
    let w = v;
    if (typeof w === 'string') {
        try {
            w = JSON.parse(w);
        } catch {
            return null;
        }
    }
    return w !== null && typeof w === 'object' && !Array.isArray(w) ? (w as Zeile) : null;
};

export const text = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** Leerer Text ist NULL — ein '' in einer Zeitspalte wäre eine Lüge. */
export const textOderNull = (v: unknown): string | null => {
    const t = text(v).trim();
    return t ? t : null;
};

/** numeric/bigint kommen über PostgREST auch als Text. */
export const zahlOderNull = (v: unknown): number | null => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? n : null;
};

export const zahl = (v: unknown, rueckfall: number): number => zahlOderNull(v) ?? rueckfall;

/**
 * Ein unbekannter Status (neuere Serverfassung, Tippfehler) wird `unklar`,
 * nicht `fehler`: der Client weiß dann nicht, ob gedruckt wurde. `unklar`
 * schickt den Menschen an den Plotter; `fehler` legte „Nochmal drucken“ nahe —
 * genau der Doppeldruck, den das Modul verhindern soll.
 */
const statusLesen = (v: unknown): PlotStatus => {
    const s = text(v).trim().toLowerCase();
    return (PLOT_STATUS_WERTE as readonly string[]).includes(s) ? (s as PlotStatus) : 'unklar';
};

export const auftragNormalisieren = (roh: unknown): PlotAuftrag | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = textOderNull(z.id);
    if (!id) return null;
    const kopien = Math.max(1, Math.floor(zahl(z.kopien, 1)));
    return {
        id,
        company_id: text(z.company_id).trim(),
        project_id: textOderNull(z.project_id),
        sige_projekt_id: textOderNull(z.sige_projekt_id),
        titel: text(z.titel),
        dateiname: text(z.dateiname),
        pdf_pfad: text(z.pdf_pfad).trim(),
        pdf_bytes: zahlOderNull(z.pdf_bytes),
        breite_mm: zahl(z.breite_mm, 0),
        hoehe_mm: zahl(z.hoehe_mm, 0),
        kopien,
        kopien_fertig: Math.min(kopien, Math.max(0, Math.floor(zahl(z.kopien_fertig, 0)))),
        status: statusLesen(z.status),
        meldung: textOderNull(z.meldung),
        erstellt_von: text(z.erstellt_von).trim(),
        erstellt_von_name: textOderNull(z.erstellt_von_name),
        erstellt_am: text(z.erstellt_am).trim(),
        abgeholt_am: textOderNull(z.abgeholt_am),
        lebenszeichen_am: textOderNull(z.lebenszeichen_am),
        fertig_am: textOderNull(z.fertig_am),
        agent_id: textOderNull(z.agent_id),
        wiederholung_von: textOderNull(z.wiederholung_von),
    };
};

/** Für die RPCs, die genau eine Zeile zurückgeben müssen. */
const auftragAusAntwort = (roh: unknown): PlotAuftrag => {
    const a = auftragNormalisieren(roh);
    if (!a) throw fehler('unbekannt', 'Der Server hat den Auftrag nicht bestätigt — bitte die Liste neu laden.');
    return a;
};

const agentNormalisieren = (roh: unknown): PlotAgent | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = textOderNull(z.id);
    if (!id) return null;
    const gesperrt = z.gesperrt === true || z.gesperrt === 'true'
        || (z.gesperrt === undefined && textOderNull(z.gesperrt_am) !== null);
    return {
        id,
        name: textOderNull(z.name) ?? 'Druck-PC',
        letzter_kontakt: textOderNull(z.letzter_kontakt),
        info: alsObjekt(z.info),
        gesperrt,
        erstellt_am: text(z.erstellt_am).trim(),
    };
};

export const anlageStatusNormalisieren = (roh: unknown): PlotAnlageStatus => {
    const z = alsObjekt(roh) ?? {};
    const agenten = Array.isArray(z.agenten)
        ? z.agenten.map(agentNormalisieren).filter((a): a is PlotAgent => a !== null)
        : [];
    return { jetzt: text(z.jetzt).trim(), company_id: textOderNull(z.company_id), agenten };
};

// ─── Ablage (Storage) ──────────────────────────────────────────────────────

export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const auftragsId = (roh: string): string => {
    const id = (roh ?? '').trim().toLowerCase();
    if (!UUID.test(id)) throw fehler('unbekannt', 'Die Auftrags-ID ist ungültig — bitte den Dialog schließen und neu senden.');
    return id;
};

export const leerZuNull = (v: string | null | undefined): string | null => {
    const t = (v ?? '').trim();
    return t ? t : null;
};

/**
 * Datei liegt schon da. storage-js 2.7 reicht bei `upload` den Körper der
 * Storage-API unverändert als `error` durch: `{ statusCode: "409", error:
 * "Duplicate", message: "The resource already exists" }` (HTTP 400 oder 409).
 * Der erste Versuch mit derselben Auftrags-ID ist also schon angekommen.
 */
const istSchonVorhanden = (e: unknown): boolean => {
    const status = text(feld(e, 'statusCode') ?? feld(e, 'status')).trim();
    if (status === '409') return true;
    if (text(feld(e, 'error')).trim().toLowerCase() === 'duplicate') return true;
    return /already exists|duplicate/i.test(fehlerText(e));
};

const hochladenText = (e: unknown): string => {
    const status = text(feld(e, 'statusCode') ?? feld(e, 'status')).trim();
    const roh = `${text(feld(e, 'error'))} ${fehlerText(e)}`;
    if (status === '413' || /maximum allowed size|too large/i.test(roh)) {
        return 'Die Datei ist zu groß für die Plotter-Ablage (höchstens 50 MB).';
    }
    if (status === '403' || /row-level security|unauthorized/i.test(roh)) {
        return 'Die Datei ließ sich nicht hochladen: keine Berechtigung für die Plotter-Ablage.';
    }
    if (/mime/i.test(roh)) return 'Die Datei ließ sich nicht hochladen: die Plotter-Ablage nimmt nur PDF-Dateien an.';
    const diag = describeError(e);
    return diag ? `${ERSATZ_TEXT.hochladen} (${diag})` : ERSATZ_TEXT.hochladen;
};

const hochladen = async (pfad: string, blob: Blob): Promise<void> => {
    let antwort: Antwort;
    try {
        antwort = alsAntwort(await supabase.storage.from(PLOT_BUCKET).upload(pfad, blob, {
            contentType: 'application/pdf',
            upsert: false,
        }));
    } catch (e) {
        // storage-js wirft einen gescheiterten fetch beim Hochladen weiter.
        throw istNetzFehler(e) ? fehler('netz') : fehler('hochladen', hochladenText(e));
    }
    const e = antwort.error;
    if (!e || istSchonVorhanden(e)) return;
    if (istNetzFehler(e)) throw fehler('netz');
    if (/bucket not found/i.test(`${text(feld(e, 'error'))} ${fehlerText(e)}`)) throw fehler('nicht_eingerichtet');
    throw fehler('hochladen', hochladenText(e));
};

/**
 * „Object not found“ — Storage unterscheidet „fehlt“ und „gesperrt“ bewusst
 * nicht (siehe projektDokumenteService.ts); für den Anwender heißt beides:
 * an diese Datei kommt er nicht mehr.
 */
const istNichtGefunden = (e: unknown): boolean => {
    const status = text(feld(e, 'statusCode') ?? feld(e, 'status')).trim();
    return status === '404' || /not.?found/i.test(`${text(feld(e, 'error'))} ${fehlerText(e)}`);
};

const LINK_FEHLER = 'Der Druck-Link für die Datei ließ sich nicht erzeugen — bitte erneut senden.';

/**
 * Signierter Link für den Druck-Agenten. `null` heißt: die Ablage kennt die
 * Datei nicht. Ohne Netz → `netz`, sonst → `hochladen`.
 */
const signierterLink = async (pfad: string): Promise<string | null> => {
    let antwort: Antwort;
    try {
        antwort = alsAntwort(await supabase.storage.from(PLOT_BUCKET).createSignedUrl(pfad, PLOT_LINK_GUELTIG_SEK));
    } catch (e) {
        throw istNetzFehler(e) ? fehler('netz') : fehler('hochladen', `${LINK_FEHLER} (${describeError(e)})`);
    }
    const e = antwort.error;
    if (e) {
        if (istNetzFehler(e)) throw fehler('netz');
        if (istNichtGefunden(e)) return null;
        throw fehler('hochladen', `${LINK_FEHLER} (${describeError(e)})`);
    }
    const url = textOderNull(feld(antwort.data, 'signedUrl'));
    if (!url) throw fehler('hochladen', LINK_FEHLER);
    return url;
};

// ═══ Senden ════════════════════════════════════════════════════════════════

/**
 * Hochladen (`upsert: false`, „existiert schon“ = Wiederholung → weiter),
 * signierter Link (PLOT_LINK_GUELTIG_SEK), `plot_auftrag_anlegen`.
 * Idempotent über `eingabe.auftragId`: ein zweiter Klick mit derselben ID
 * findet die Datei schon vor und bekommt vom Server die bestehende Zeile.
 */
export async function plotAuftragSenden(eingabe: PlotSendenEingabe): Promise<PlotAuftrag> {
    const id = auftragsId(eingabe.auftragId);
    const kopien = kopienBegrenzen(eingabe.kopien);

    const status = await plotAnlageStatusLaden();
    if (!status) throw fehler('nicht_eingerichtet');
    if (!status.company_id) throw fehler('keine_firma');

    const pfad = plotPfad(status.company_id, id);
    await hochladen(pfad, eingabe.blob);

    // Gerade hochgeladen und trotzdem „nicht gefunden“: Leserecht fehlt.
    const link = await signierterLink(pfad);
    if (!link) throw fehler('hochladen', LINK_FEHLER);

    const daten = await rufe('plot_auftrag_anlegen', {
        p_id: id,
        p_project_id: leerZuNull(eingabe.projectId),
        p_sige_projekt_id: leerZuNull(eingabe.sigeProjektId),
        p_titel: eingabe.titel,
        p_dateiname: eingabe.dateiname,
        p_pdf_pfad: pfad,
        p_pdf_url: link,
        p_pdf_bytes: eingabe.blob.size,
        p_breite_mm: eingabe.breiteMm,
        p_hoehe_mm: eingabe.hoeheMm,
        p_kopien: kopien,
    });
    return auftragAusAntwort(daten);
}

/**
 * Neuer Auftrag auf dieselbe Datei (`alt.pdf_pfad`) mit frischem Link und
 * `wiederholung_von` — kein zweites Hochladen. Maße, Titel, Dateiname und
 * Größe wie beim alten Auftrag.
 */
export async function plotAuftragWiederholen(alt: PlotAuftrag, neueId: string, kopien: number): Promise<PlotAuftrag> {
    const id = auftragsId(neueId);
    const anzahl = kopienBegrenzen(kopien);
    const link = alt.pdf_pfad ? await signierterLink(alt.pdf_pfad) : null;
    if (!link) {
        throw fehler('hochladen', 'Die Datei dieses Auftrags ist nicht mehr vorhanden — bitte aus der Vorschau neu senden.');
    }
    const daten = await rufe('plot_auftrag_anlegen', {
        p_id: id,
        p_project_id: leerZuNull(alt.project_id),
        p_sige_projekt_id: leerZuNull(alt.sige_projekt_id),
        p_titel: alt.titel,
        p_dateiname: alt.dateiname,
        p_pdf_pfad: alt.pdf_pfad,
        p_pdf_url: link,
        p_pdf_bytes: alt.pdf_bytes,
        p_breite_mm: alt.breite_mm,
        p_hoehe_mm: alt.hoehe_mm,
        p_kopien: anzahl,
        p_wiederholung_von: alt.id,
    });
    return auftragAusAntwort(daten);
}

// ═══ Lesen ═════════════════════════════════════════════════════════════════

const LADEN_STANDARD = 10;
const LADEN_MAX = 100;

/**
 * Neueste zuerst. Ohne Filter: alle der Firma (RLS). `limit` Standard 10.
 * Gefiltert wird nach `sige_projekt_id`, wenn gesetzt, sonst nach
 * `project_id` — nie nach beiden: ein Plan, der erst später mit einem Projekt
 * verknüpft wurde, hätte frühere Aufträge ohne `project_id`.
 * `ohneSigePlan` (nur mit `project_id`): bloß die Aufträge des Projekts ohne
 * SiGe-Plan — die geplottete Vorankündigung. Ihre Liste zeigte sonst auch
 * die Pläne des Projekts, und „Nochmal drucken“ dort druckte einen Plan.
 * Fehlt die Tabelle (SQL Nr. 157 offen), ist die Liste leer.
 */
export async function plotAuftraegeLaden(filter: {
    projectId?: string | null;
    sigeProjektId?: string | null;
    ohneSigePlan?: boolean;
    limit?: number;
}): Promise<PlotAuftrag[]> {
    const roh = filter?.limit;
    const limit = typeof roh === 'number' && Number.isFinite(roh)
        ? Math.min(LADEN_MAX, Math.max(1, Math.floor(roh)))
        : LADEN_STANDARD;
    const sige = leerZuNull(filter?.sigeProjektId);
    const projekt = leerZuNull(filter?.projectId);

    let antwort: Antwort;
    try {
        let abfrage = supabase.from(TABELLE).select(PLOT_AUFTRAG_SPALTEN);
        if (sige) abfrage = abfrage.eq('sige_projekt_id', sige);
        else if (projekt) {
            abfrage = abfrage.eq('project_id', projekt);
            if (filter?.ohneSigePlan === true) abfrage = abfrage.is('sige_projekt_id', null);
        }
        antwort = alsAntwort(await abfrage.order('erstellt_am', { ascending: false }).limit(limit));
    } catch (e) {
        throw alsPlotFehler(e);
    }
    if (antwort.error) {
        const f = alsPlotFehler(antwort.error, antwort.status);
        if (f.code === 'nicht_eingerichtet') return [];
        throw f;
    }
    if (!Array.isArray(antwort.data)) return [];
    return antwort.data.map(auftragNormalisieren).filter((a): a is PlotAuftrag => a !== null);
}

/**
 * Gibt es diesen Auftrag (eigene Firma, RLS)? Gezielt über die ID — die Liste
 * zeigt nur die neuesten, ein angekommener Auftrag kann außerhalb liegen.
 * `null` heißt: sicher nicht da. Jede Unsicherheit wirft, auch fehlendes SQL —
 * nur bei `null` darf eine Sendeabsicht verworfen werden.
 */
export async function plotAuftragFinden(id: string): Promise<PlotAuftrag | null> {
    const schluessel = auftragsId(id);
    let antwort: Antwort;
    try {
        antwort = alsAntwort(await supabase.from(TABELLE).select(PLOT_AUFTRAG_SPALTEN).eq('id', schluessel).maybeSingle());
    } catch (e) {
        throw alsPlotFehler(e);
    }
    if (antwort.error) throw alsPlotFehler(antwort.error, antwort.status);
    if (antwort.data === null) return null;
    const a = auftragNormalisieren(antwort.data);
    if (!a) throw fehler('unbekannt', 'Die Antwort zum Auftrag ließ sich nicht lesen — bitte später erneut prüfen.');
    return a;
}

/** Nur aus `wartet` (sonst `nicht_mehr_wartend`); Ersteller oder Admin. */
export async function plotAuftragAbbrechen(id: string): Promise<PlotAuftrag> {
    return auftragAusAntwort(await rufe('plot_auftrag_abbrechen', { p_id: id }));
}

/** `unklar` (oder hängendes `druckt`) von Hand klären: true → `gedruckt`, false → `fehler`. */
export async function plotAuftragKlaeren(id: string, gedruckt: boolean): Promise<PlotAuftrag> {
    return auftragAusAntwort(await rufe('plot_auftrag_klaeren', { p_id: id, p_gedruckt: gedruckt === true }));
}

/**
 * `null` = SQL Nr. 157/158 fehlt in dieser Umgebung (PGRST202 / 42883 / 404).
 * Sonst wirft es nur bei echten Fehlern. Merkt sich die Empfangszeit der
 * Antwort (Geräteuhr), damit `erreichbarkeit` gegen die Serveruhr rechnet.
 */
export async function plotAnlageStatusLaden(): Promise<PlotAnlageStatus | null> {
    let daten: unknown;
    try {
        daten = await rufe('plot_status');
    } catch (e) {
        if (e instanceof PlotFehler && e.code === 'nicht_eingerichtet') return null;
        throw e;
    }
    const status = anlageStatusNormalisieren(daten);
    statusEmpfangenMerken(status, Date.now());
    return status;
}

// ═══ Verwaltung (Admin) ═══════════════════════════════════════════════════

const AGENT_NAME_MAX = 80;

/** Legt einen Druck-PC an. Der Schlüssel (`token`) steht NUR in dieser Antwort. */
export async function plotAgentAnlegen(name: string): Promise<PlotAgentNeu> {
    const sauber = (name ?? '').replace(/\s+/g, ' ').trim();
    if (!sauber) throw fehler('unbekannt', 'Bitte einen Namen für den Druck-PC eingeben.');
    if (sauber.length > AGENT_NAME_MAX) {
        throw fehler('unbekannt', `Der Name des Druck-PCs darf höchstens ${AGENT_NAME_MAX} Zeichen haben.`);
    }
    const z = alsObjekt(await rufe('plot_agent_anlegen', { p_name: sauber }));
    const id = textOderNull(z?.id);
    const token = textOderNull(z?.token);
    if (!z || !id || !token) {
        throw fehler('unbekannt', 'Der Server hat keinen Schlüssel für den Druck-PC geliefert — bitte erneut anlegen.');
    }
    return { id, name: textOderNull(z.name) ?? sauber, token };
}

/** Sperrt einen Druck-PC; er holt danach nichts mehr ab. */
export async function plotAgentSperren(id: string): Promise<void> {
    await rufe('plot_agent_sperren', { p_id: id });
}
